import { action, observable } from "mobx";
import { Index, IndexRange, InfiniteLoader, Table } from "react-virtualized";

type FetchCallback = (skip?: number, limit?: number) => Promise<any>;

interface ListHolder {
  scrollToPosition: (scrollTop: number) => void;
  scrollToRow: (index: number) => void;
}

/**
 * @deprecated outdated functionality. Use InfiniteScrollSearchManager with <InfiniteScroll />
 */
export class VirtualSearchManager<ROW extends { _id: string }> {
  @observable protected _isLoading = false;

  protected _items: ROW[] = [];
  protected _refresh: () => void;
  protected _listHolder: ListHolder;
  protected _infiniteLoader: InfiniteLoader;
  protected _scrollContainer: HTMLElement | null = null;

  constructor(private _fetch: FetchCallback, lazy = false) {
    !lazy && this.fetch();
  }

  get rowCount() {
    return this._items.length;
  }

  get items() {
    return this._items;
  }

  get refresh() {
    return this._refresh;
  }

  get isLoading() {
    return this._isLoading;
  }

  setLoading = (v: boolean) => {
    this._isLoading = v;
  };

  fetch: FetchCallback = (skip?, limit?) => {
    this._isLoading = true;

    return this._fetch(skip, limit).finally(() => {
      this._isLoading = false;
    });
  };

  loadMoreRows = (params: IndexRange) => {
    const skip = params.startIndex;
    const limit = params.stopIndex - params.startIndex;

    return this.fetch(skip, limit);
  };

  getIndexById = (id: string) => {
    return this._items.findIndex((x) => x._id === id);
  };

  keyMapper = (index: number) => {
    return this._items[index]?._id || "";
  };

  getRow = (index: number) => {
    return this._items[index];
  };

  registerUpdateCallback = (cb: () => void) => {
    this._refresh = cb;
  };

  setListHolder = (ref: Table) => {
    this._listHolder = ref;
  };

  setScrollContainer = (el: HTMLElement) => {
    this._scrollContainer = el;
  };

  isRowLoaded = (params: Index) => {
    return this._items.length > params.index;
  };

  @action.bound clear(preserveScrollPosition = false) {
    if (!preserveScrollPosition) {
      this.scrollTop();
    }
    this._items = [];
    this._infiniteLoader && this._infiniteLoader.resetLoadMoreRowsCache();
  }

  @action.bound iLoaderForceUpdate() {
    this._infiniteLoader && this._infiniteLoader.forceUpdate();
  }

  scrollTop = () => {
    if (this._scrollContainer) {
      this._scrollContainer.scrollTop = 0;
    } else {
      this._listHolder && this._listHolder.scrollToPosition(0);
    }
  };

  updateRow = (index: number, val: ROW) => {
    this._items[index] = val;
  };

  prependRow = (val: ROW) => {
    this._items.unshift(val);
  };

  deleteRow = (index: number) => {
    this._items.splice(index, 1);
  };

  push = (rows: ROW[]) => this._items.push(...rows);

  merge = (rows: ROW[]) => {
    console.log("merge single");
    rows.forEach((row) => {
      const index = this._items.findIndex((x) => x._id === row._id);
      if (index >= 0) {
        this._items[index] = row;
      } else {
        this._items.push(row);
      }
    });
    this.refresh();
  };

  unshift = (row: ROW) => this._items.unshift(row);

  setInfiniteLoaderRef = (ref) => ref && (this._infiniteLoader = ref);
}
